import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { createPortal } from "react-dom";
import Icon from "../utils/icons";
import { referralCode } from "../utils/referral";
import { PLACEHOLDER_IMG, money, sizedImage } from "../utils/formatters";
import { selectStoreConfig } from "../redux/Slice/siteSlice";

// "Share" on a product, for the store's marketing: copy the product's link or send it to WhatsApp, Facebook or
// Telegram (and the phone's own share sheet where there is one). The link opens this product in the store.
export function productUrl(product) {
    const code = referralCode(); // a partner's referral code stays on links shared from a demo store
    return `${window.location.origin}/product/${product.id}${code ? `?ref=${code}` : ""}`;
}

export default function ProductShare({ product, variant = "icon" }) {
    const config = useSelector(selectStoreConfig);
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const url = productUrl(product);
    const text = product ? `${product.name} – ${money(product.price)}` : "";

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    // On demo stores, hide the share icon/button as requested
    if (config?.isDemo || config?.showOwnezzyOffer) return null;

    // The share button sits on clickable product cards, so its clicks must not open the product
    const stop = (e) => e.stopPropagation();
    const toggle = (e) => { stop(e); setCopied(false); setOpen((o) => !o); };
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
        } catch {
            window.prompt("Copy this link", url);
        }
    };
    const nativeShare = async () => {
        try { await navigator.share({ title: product.name, text, url }); setOpen(false); } catch { /* closed */ }
    };
    const targets = [
        { label: "WhatsApp", icon: <Icon.Whatsapp />, href: `https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}` },
        { label: "Facebook", icon: <Icon.Facebook />, href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` },
        { label: "Telegram", icon: <Icon.Telegram />, href: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}` },
    ];

    return (
        <>
            {variant === "icon" ? (
                <button type="button" className="productShareIcon" aria-label={`Share ${product.name}`} onClick={toggle} onPointerDown={stop}><Icon.ShareFill /></button>
            ) : (
                <button type="button" className="productShareButton" onClick={toggle}><Icon.ShareFill /> Share</button>
            )}
            {/* Rendered into <body>: product cards are transformed on hover, which would trap a fixed sheet inside the
                card. React still bubbles its events to the card, hence stop(). */}
            {open && createPortal(
                <div className="productShareBackdrop" role="presentation" onClick={(e) => { stop(e); if (e.target === e.currentTarget) setOpen(false); }} onPointerDown={stop}>
                    <div className="productShareSheet" role="dialog" aria-modal="true" aria-label={`Share ${product.name}`}>
                        <div className="productShareHead">
                            <b>Share this product</b>
                            <button type="button" onClick={() => setOpen(false)} aria-label="Close"><Icon.X /></button>
                        </div>
                        <div className="productShareItem">
                            <img src={product.img ? sizedImage(product.img, 200) : PLACEHOLDER_IMG} alt="" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = PLACEHOLDER_IMG; }} />
                            <div><b>{product.name}</b><span>{money(product.price)}</span></div>
                        </div>
                        <div className="productShareLink">
                            <input readOnly value={url} onFocus={(e) => e.target.select()} aria-label="Product link" />
                            <button type="button" onClick={copy}>{copied ? <><Icon.Check2 /> Copied</> : <><Icon.Link45deg /> Copy link</>}</button>
                        </div>
                        <div className="productShareTargets">
                            {targets.map((t) => <a key={t.label} href={t.href} target="_blank" rel="noopener noreferrer" className={`share-${t.label.toLowerCase()}`}>{t.icon}<span>{t.label}</span></a>)}
                            {typeof navigator.share === "function" && <button type="button" onClick={nativeShare}><Icon.ThreeDots /><span>More</span></button>}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}
